<template>
  <div class="workbench-shell">
    <div v-if="!isLoggedIn" class="workbench-login">
      <div class="login-card">
        <div class="brand-mark">W</div>
        <h1>个人工作台</h1>
        <p>登录后创建、编辑并管理你的 AI 工作台</p>
        <button class="login-redirect-button" type="button" @click="redirectToLogin">前往登录</button><a class="back-link" href="/?"
          >返回 AI 助手</a
        >
      </div>
    </div>
    <template v-else>
      <aside
        class="workbench-sidebar"
        :class="{ 'mobile-open': mobileNav }"
      >
        <div class="sidebar-brand">
          <div class="brand-mark small">W</div>
          <div><strong>个人工作台</strong><span>AI Workspace</span></div>
          <button
            class="sidebar-close"
            type="button"
            title="关闭侧边栏"
            aria-label="关闭侧边栏"
            @click="mobileNav = false"
          >
            ×
          </button>
        </div>
        <button
          class="sidebar-home"
          :class="{ active: activeTab === 'home' && !selected }"
          @click="showHome"
        >
          <span class="tab-icon">⌂</span><span>主页</span>
        </button>
        <button class="new-workbench" @click="resetCreate">
          <span>+</span>新建工作台
        </button>
        <nav class="workbench-tabs project-tabs" aria-label="我的工作台">
          <span class="projects-label"
            >我的工作台 <em>{{ workbenches.length }}</em></span
          >
          <button
            v-for="item in workbenches"
            :key="item.id"
            :class="{ active: selected && selected.id === item.id }"
            :title="item.name"
            @click="openWorkbench(item.id)"
          >
            <span class="tab-icon">◻</span
            ><span class="project-name">{{ item.name }}</span>
            <span class="project-status" :class="item.status || 'ready'"></span>
          </button>
          <span v-if="loading" class="projects-loading">正在加载…</span>
          <button
            v-else-if="!workbenches.length"
            class="empty-project"
            @click="resetCreate"
          >
            <span class="tab-icon">+</span>创建第一个工作台
          </button>
        </nav>
        <div class="sidebar-foot">
          <a href="/?">返回 AI 助手</a><button @click="logout">退出登录</button>
        </div>
      </aside>
      <button
        v-if="mobileNav"
        class="sidebar-backdrop"
        type="button"
        aria-label="关闭侧边栏"
        @click="mobileNav = false"
      ></button>
      <main
        class="workbench-main"
        :class="{ 'preview-main': activeTab === 'preview' && selected }"
      >
        <header class="workbench-header">
          <div class="header-leading">
            <button
              class="mobile-back-button"
              type="button"
              title="后退"
              aria-label="后退"
              @click="goBack"
            >
              <svg viewBox="0 0 24 24" aria-hidden="true">
                <path d="M15 18l-6-6 6-6" />
              </svg>
            </button>
            <div>
              <span class="eyebrow">PERSONAL WORKSPACE</span>
              <h1>
                {{
                  activeTab === "create"
                    ? "创建一个新工作台"
                    : activeTab === "home"
                    ? "主页"
                    : selected
                    ? selected.name
                    : "个人工作台"
                }}
              </h1>
            </div>
          </div>
          <div class="header-actions">
            <span v-if="notice" :class="['notice', noticeType]">{{
              notice
            }}</span
            ><button class="mobile-menu" @click="mobileNav = !mobileNav">
              ☰
            </button>
          </div>
        </header>
        <section v-if="activeTab === 'home' && !selected" class="workspace-list">
          <div class="list-intro">
            <div>
              <h2>把想法变成一个可用页面</h2>
              <p>输入你需要的工具，AI 会生成一个独立的单 HTML 工作台。</p>
            </div>
            <button class="primary-btn" @click="resetCreate">
              + 新建工作台
            </button>
          </div>
          <div v-if="loading" class="loading-state">正在加载工作台…</div>
          <div v-else-if="!workbenches.length" class="empty-state">
            <div class="empty-icon">✦</div>
            <h3>还没有工作台</h3>
            <p>从一句“帮我做一个记账本”开始。</p>
            <button class="primary-btn" @click="resetCreate">开始创建</button>
          </div>
          <div v-else class="workspace-grid">
            <article
              v-for="item in workbenches"
              :key="item.id"
              class="workspace-card"
              @click="openWorkbench(item.id)"
            >
              <div class="card-preview">
                <iframe
                  v-if="previewSources[item.id]"
                  :srcdoc="previewSources[item.id]"
                  sandbox="allow-scripts allow-forms"
                  title="工作台预览"
                  loading="lazy"
                ></iframe>
                <div v-else class="preview-placeholder">HTML 预览</div>
              </div>
              <div class="card-body">
                <div class="card-title">
                  <h3>{{ item.name }}</h3>
                  <span class="status-dot"></span>
                  <button
                    class="rename-workbench"
                    type="button"
                    title="重命名工作台"
                    aria-label="重命名工作台"
                    @click.stop="beginRename(item)"
                  >
                    ✎
                  </button>
                  <button
                    class="delete-workbench"
                    title="删除工作台"
                    aria-label="删除工作台"
                    @click.stop="removeWorkbench(item)"
                  >
                    ×
                  </button>
                </div>
                <p>{{ item.prompt }}</p>
                <time>{{ formatDate(item.updated_at) }}</time>
              </div>
            </article>
          </div>
        </section>
        <section
          v-else-if="activeTab === 'create' && !selected"
          class="create-panel"
        >
          <div class="create-copy">
            <span class="step-label">STEP 01</span>
            <h2>你想要什么工作台？</h2>
            <p>
              用自然语言描述日常需求。AI
              会补齐信息架构、交互和移动端适配，生成后你还可以在线编辑源码。
            </p>
          </div>
          <form class="prompt-form" @submit.prevent="create">
            <label for="workbench-prompt">需求描述</label
            ><textarea
              id="workbench-prompt"
              v-model.trim="form.prompt"
              rows="7"
              placeholder="例如：帮我做一个记账本，能记录收入支出、按分类统计，并显示本月结余。页面要简洁，手机上方便快速录入。"
            ></textarea>
            <div class="form-row single-column">
              <label
                >工作台名称<input
                  v-model.trim="form.name"
                  maxlength="32"
                  placeholder="留空让 AI 自动命名" /></label
              >
            </div>
            <details class="advanced-config">
              <summary>
                <span>自定义 AI 接口（可选）</span>
                <label class="custom-ai-toggle" @click.stop>
                  <input v-model="form.useCustomAi" type="checkbox" @click.stop />
                  <span>启用</span>
                </label>
              </summary>
              <fieldset class="form-row" :disabled="!form.useCustomAi">
                <label
                  >Endpoint<input
                    v-model.trim="form.endpoint"
                    placeholder="默认使用系统配置" /></label
                ><label
                  >API Key<input
                    v-model.trim="form.apiKey"
                    type="password"
                    autocomplete="off"
                    placeholder="仅本次生成使用，不会写入页面"
                  /></label>
              </fieldset>
            </details>
            <button
              class="primary-btn create-btn"
              type="submit"
              :disabled="creating || !form.prompt"
            >
              {{ creating ? "AI 正在生成…" : "生成我的工作台" }} <span>→</span>
            </button>
          </form>
          <div class="prompt-examples">
            <span>可以这样说</span
            ><button
              v-for="example in examples"
              :key="example"
              @click="form.prompt = example"
            >
              {{ example }}
            </button>
          </div>
          <div class="import-project">
            <div>
              <strong>已有项目</strong
              ><span>导入单文件 HTML 或包含 index.html 的 ZIP</span>
            </div>
            <input
              ref="importFile"
              class="import-file"
              type="file"
              accept=".html,.htm,.zip,text/html,application/zip"
              @change="importProject"
            />
            <button
              type="button"
              class="import-button"
              :disabled="importing"
              @click="$refs.importFile.click()"
            >
              {{ importing ? "正在导入…" : "导入 HTML / ZIP" }}
            </button>
          </div>
        </section>
        <section
          v-if="activeTab === 'preview'"
          class="workspace-stage"
          :class="{ 'canvas-stage': selected && viewMode === 'preview' }"
        >
          <div v-if="!selected" class="stage-empty">
            <div class="empty-icon">✦</div>
            <h2>选择一个工作台</h2>
            <p>从左侧切换已有工作台，或创建一个新的工具。</p>
            <button class="primary-btn" @click="resetCreate">新建工作台</button>
          </div>
          <template v-else>
            <header class="stage-toolbar">
              <div class="stage-title">
                <button
                  class="mobile-back-button"
                  type="button"
                  title="后退"
                  aria-label="后退"
                  @click="goBack"
                >
                  <svg viewBox="0 0 24 24" aria-hidden="true">
                    <path d="M15 18l-6-6 6-6" />
                  </svg>
                </button>
                <button
                  class="mobile-menu stage-menu"
                  type="button"
                  title="打开侧边栏"
                  aria-label="打开侧边栏"
                  @click="mobileNav = true"
                >
                  ☰
                </button>
                <span
                  class="status-dot"
                  :class="{ pending: isGenerating, failed: generationFailed }"
                ></span>
                <div>
                  <div class="stage-title-name">
                    <h1>{{ selected.name }}</h1>
                    <button
                      class="rename-title-action"
                      type="button"
                      title="重命名工作台"
                      aria-label="重命名工作台"
                      @click="beginRename(selected)"
                    >
                      ✐
                    </button>
                  </div>
                  <span>{{
                    isGenerating
                      ? "后台生成中"
                      : generationFailed
                      ? "生成失败"
                      : "已发布工作台"
                  }}</span>
                </div>
              </div>
              <div class="stage-actions">
                <button
                  v-if="
                    !isGenerating && !generationFailed && viewMode === 'preview'
                  "
                  class="icon-action"
                  title="编辑工作台"
                  aria-label="编辑工作台"
                  @click="viewMode = 'edit'"
                >
                  ✎
                </button>
                <button
                  v-if="viewMode === 'edit'"
                  class="text-action"
                  :disabled="modifying"
                  @click="viewMode = 'preview'"
                >
                  完成编辑
                </button>
                <button
                  v-if="viewMode === 'edit'"
                  class="danger-action"
                  :disabled="modifying"
                  @click="removeWorkbench(selected)"
                >
                  删除工作台
                </button>
              </div>
            </header>
            <section
              v-if="viewMode === 'regenerate'"
              class="regenerate-panel"
            >
              <div class="regenerate-heading">
                <h2>重试生成工作台</h2>
                <p>修正需求后重新尝试，仍使用当前工作台 ID。</p>
              </div>
              <label>
                工作台名称
                <input v-model.trim="regenerateForm.name" maxlength="32" />
              </label>
              <label>
                需求描述
                <textarea
                  v-model.trim="regenerateForm.prompt"
                  maxlength="4000"
                  rows="8"
                  placeholder="描述想要生成或调整的功能"
                ></textarea>
              </label>
              <details class="advanced-config">
                <summary>
                  <span>AI 配置</span>
                  <label class="custom-ai-toggle" @click.stop>
                    <input v-model="regenerateForm.useCustomAi" type="checkbox" @click.stop />
                    <span>启用</span>
                  </label>
                </summary>
                <div class="form-row">
                  <label>接口地址<input v-model.trim="regenerateForm.endpoint" :disabled="!regenerateForm.useCustomAi" /></label>
                  <label>模型<input v-model.trim="regenerateForm.model" :disabled="!regenerateForm.useCustomAi" /></label>
                </div>
                <label>API Key<input v-model.trim="regenerateForm.apiKey" type="password" autocomplete="off" :disabled="!regenerateForm.useCustomAi" /></label>
              </details>
              <div class="regenerate-actions">
                <button class="text-action" @click="viewMode = 'preview'">取消</button>
                <button
                  class="primary-btn"
                  :disabled="regenerating || !regenerateForm.prompt"
                  @click="submitRegenerate"
                >
                  {{ regenerating ? "提交中…" : "重新尝试生成" }}
                </button>
              </div>
            </section>
            <div v-else-if="isGenerating" class="generation-state stage-status">
              <span class="generation-spinner"></span>
              <h2>正在后台生成工作台</h2>
              <p>任务完成后会自动显示在这里。</p>
              <small>{{
                selected.status === "queued"
                  ? "任务排队中"
                  : "正在调用模型并发布"
              }}</small>
            </div>
            <div
              v-else-if="generationFailed"
              class="generation-state failed-state stage-status"
            >
              <h2>生成失败</h2>
              <p>{{ selected.error_message || "服务器没有返回详细原因" }}</p>
              <div class="failed-actions">
                <button class="primary-btn" @click="beginRegenerate">重新尝试</button>
                <button class="danger-action" @click="removeWorkbench(selected)">
                  删除项目
                </button>
              </div>
            </div>
            <template v-else>
              <div v-if="contentLoading" class="generation-state stage-status">
                <span class="generation-spinner"></span>
                <h2>正在加载工作台</h2>
              </div>
              <div v-else-if="viewMode === 'preview'" class="workbench-canvas">
                <iframe
                  :key="`${selected.id}-${selected.updated_at}`"
                  :srcdoc="source"
                  sandbox="allow-scripts allow-forms allow-downloads"
                  title="工作台内容"
                ></iframe>
              </div>
              <div v-else class="edit-workspace">
                <section class="ai-modify-panel">
                  <form class="ai-modify-bar" @submit.prevent="submitModification">
                    <label for="workbench-modification">让 AI 继续修改</label>
                    <textarea
                      id="workbench-modification"
                      ref="modificationInput"
                      v-model.trim="modification"
                      :disabled="modifying"
                      maxlength="4000"
                      rows="2"
                      placeholder="例如：增加按月份筛选，并把统计放到页面顶部"
                    ></textarea>
                    <button
                      v-if="!modifying"
                      class="primary-btn"
                      type="submit"
                      :disabled="!modification"
                    >
                      {{ modificationSteps.length ? "继续修改" : "开始修改" }}
                    </button>
                    <button
                      v-else-if="!modificationPublishing"
                      class="text-action stop-modification"
                      type="button"
                      @click="cancelModification"
                    >
                      停止
                    </button>
                    <button v-else class="primary-btn" type="button" disabled>
                      正在校验并保存…
                    </button>
                  </form>
                  <div
                    v-if="modificationSteps.length"
                    ref="modificationSteps"
                    class="agent-steps"
                    aria-live="polite"
                  >
                    <div
                      v-for="(step, index) in modificationSteps"
                      :key="index"
                      class="agent-step"
                      :class="{ failed: step.failed, pending: step.pending }"
                    >
                      <span class="agent-step-index">{{ index + 1 }}</span>
                      <div>
                        <strong>{{ step.title }}</strong>
                        <p>{{ step.message }}</p>
                        <details v-if="step.detail">
                          <summary>查看命令与结果</summary>
                          <pre>{{ step.detail }}</pre>
                        </details>
                      </div>
                    </div>
                  </div>
                </section>
                <div class="editor-toolbar compact">
                  <div class="editor-title">
                    <strong>源码编辑</strong
                    ><span>{{ source.length.toLocaleString() }} chars</span>
                  </div>
                  <div class="toolbar-actions">
                    <button @click="refreshPreview">刷新预览</button>
                    <button
                      class="primary-btn small"
                      @click="save"
                      :disabled="saving || modifying || selected.status !== 'ready'"
                    >
                      {{ saving ? "保存中…" : "保存并发布" }}
                    </button>
                  </div>
                </div>
                <div class="editor-layout">
                  <div class="code-pane">
                    <div class="pane-head"><span>index.html</span></div>
                    <textarea
                      v-model="source"
                      :disabled="modifying"
                      spellcheck="false"
                      aria-label="HTML 源码编辑器"
                    ></textarea>
                  </div>
                  <div class="preview-pane">
                    <div class="pane-head"><span>实时预览</span></div>
                    <iframe
                      ref="preview"
                      sandbox="allow-scripts allow-forms"
                      :srcdoc="source"
                      title="工作台实时预览"
                    ></iframe>
                  </div>
                </div>
                <div class="versions-panel">
                  <div class="versions-head">
                    <div>
                      <h3>版本历史</h3>
                      <p>云端保留最近 10 个版本，可随时恢复。</p>
                    </div>
                    <button @click="loadVersions">刷新</button>
                  </div>
                  <div v-if="versions.length" class="version-list">
                    <div
                      v-for="version in versions"
                      :key="version.id"
                      class="version-row"
                    >
                      <div>
                        <strong>{{ version.label || "自动保存" }}</strong
                        ><span
                          >{{ formatDate(version.created_at) }} ·
                          {{ version.size || 0 }} bytes</span
                        >
                      </div>
                      <button @click="restore(version.id)">恢复此版本</button>
                    </div>
                  </div>
                  <div v-else class="versions-empty">
                    保存后会在这里看到版本记录
                  </div>
                </div>
              </div>
            </template>
          </template>
        </section>
        <section v-if="false" class="editor-view">
          <div class="editor-toolbar">
            <button class="back-button" @click="closeEditor">
              ← 工作台列表
            </button>
            <div class="editor-title">
              <span
                class="status-dot"
                :class="{ pending: isGenerating, failed: generationFailed }"
              ></span
              ><strong>{{ selected.name }}</strong
              ><span>{{
                isGenerating
                  ? "后台生成中"
                  : generationFailed
                  ? "生成失败"
                  : `最后保存于 ${formatDate(selected.updated_at)}`
              }}</span>
            </div>
            <div class="toolbar-actions">
              <button
                v-if="!isGenerating && !generationFailed"
                @click="previewOpen = !previewOpen"
              >
                {{ previewOpen ? "隐藏预览" : "显示预览" }}</button
              ><button
                class="primary-btn small"
                @click="save"
                :disabled="saving || selected.status !== 'ready'"
              >
                {{ saving ? "保存中…" : "保存并发布" }}
              </button>
            </div>
          </div>
          <div v-if="isGenerating" class="generation-state">
            <span class="generation-spinner"></span>
            <h2>正在后台生成工作台</h2>
            <p>可以离开此页面，任务完成后会出现在工作台列表中。</p>
            <small>{{
              selected.status === "queued"
                ? "任务排队中"
                : "正在调用模型并发布"
            }}</small>
          </div>
          <div
            v-else-if="generationFailed"
            class="generation-state failed-state"
          >
            <h2>生成失败</h2>
            <p>{{ selected.error_message || "服务器没有返回详细原因" }}</p>
            <button class="primary-btn" @click="resetCreate">重新创建</button>
          </div>
          <div
            v-else
            class="editor-layout"
            :class="{ 'preview-hidden': !previewOpen }"
          >
            <div class="code-pane">
              <div class="pane-head">
                <span>index.html</span
                ><span>{{ source.length.toLocaleString() }} chars</span>
              </div>
              <textarea
                v-model="source"
                spellcheck="false"
                aria-label="HTML 源码编辑器"
              ></textarea>
            </div>
            <div v-if="previewOpen" class="preview-pane">
              <div class="pane-head">
                <span>实时预览</span
                ><button @click="refreshPreview">刷新</button>
              </div>
              <iframe
                ref="preview"
                sandbox="allow-scripts"
                :srcdoc="source"
                title="工作台实时预览"
              ></iframe>
            </div>
          </div>
          <div v-if="!isGenerating && !generationFailed" class="versions-panel">
            <div class="versions-head">
              <div>
                <h3>版本历史</h3>
                <p>云端保留最近 10 个版本，可随时恢复。</p>
              </div>
              <button @click="loadVersions">刷新</button>
            </div>
            <div v-if="versions.length" class="version-list">
              <div
                v-for="version in versions"
                :key="version.id"
                class="version-row"
              >
                <div>
                  <strong>{{ version.label || "自动保存" }}</strong
                  ><span
                    >{{ formatDate(version.created_at) }} ·
                    {{ version.size || 0 }} bytes</span
                  >
                </div>
                <button @click="restore(version.id)">恢复此版本</button>
              </div>
            </div>
            <div v-else class="versions-empty">保存后会在这里看到版本记录</div>
          </div>
        </section>
      </main>
      <div
        v-if="renameTarget"
        class="rename-modal-backdrop"
        @click.self="cancelRename"
      >
        <form class="rename-modal" @submit.prevent="submitRename">
          <h2>重命名工作台</h2>
          <label for="workbench-rename">工作台名称</label>
          <input
            id="workbench-rename"
            ref="renameInput"
            v-model.trim="renameName"
            type="text"
            maxlength="32"
            autocomplete="off"
            :disabled="renaming"
            @keydown.esc="cancelRename"
          />
          <div class="rename-modal-actions">
            <button type="button" class="text-action" :disabled="renaming" @click="cancelRename">
              取消
            </button>
            <button class="primary-btn small" type="submit" :disabled="renaming || !renameName">
              {{ renaming ? "保存中…" : "保存" }}
            </button>
          </div>
        </form>
      </div>
    </template>
  </div>
</template>

<script>
import { store, mutations } from "@/store";
import { redirectToRainseeLogin } from "@/utils/loginRedirect";
import { showConfirm } from "@/utils/dialog";
import {
  listWorkbenches,
  getWorkbench,
  getWorkbenchContent,
  getWorkbenchVersions,
  createWorkbench,
  regenerateWorkbench,
  modifyWorkbenchStep,
  importWorkbench,
  saveWorkbench,
  restoreWorkbench,
  renameWorkbench,
  deleteWorkbench,
} from "@/api/workbench";

const CUSTOM_AI_ENABLED_STORAGE_KEY = "workbench.useCustomAi";

function getStoredCustomAiEnabled() {
  try {
    return (
      typeof window !== "undefined" &&
      window.localStorage.getItem(CUSTOM_AI_ENABLED_STORAGE_KEY) === "true"
    );
  } catch (error) {
    return false;
  }
}

export default {
  name: "WorkbenchPage",
  data() {
    const active = store.settings && store.settings.activeTextModelId;
    const model =
      (store.settings.models || []).find((item) => item.id === active) || {};
    const useCustomAi = getStoredCustomAiEnabled();
    return {
      store,
      activeTab: "home",
      viewMode: "preview",
      mobileNav: false,
      workbenches: [],
      selected: null,
      versions: [],
      source: "",
      previewSources: {},
      contentLoading: false,
      modification: "",
      modificationSteps: [],
      modificationRunId: 0,
      modificationAbortController: null,
      modificationPublishing: false,
      loading: false,
      creating: false,
      regenerating: false,
      modifying: false,
      importing: false,
      saving: false,
      renaming: false,
      renameTarget: null,
      renameName: "",
      previewOpen: true,
      notice: "",
      noticeType: "success",
      generationPoll: null,
      generationPolling: false,
      form: {
        name: "",
        prompt: "",
        endpoint: model.endpoint || "",
        model: model.model || "",
        apiKey: model.apiKey || "",
        useCustomAi,
      },
      regenerateForm: {
        name: "",
        prompt: "",
        endpoint: model.endpoint || "",
        model: model.model || "",
        apiKey: model.apiKey || "",
        useCustomAi,
      },
      examples: [
        "帮我做一个记账本，支持分类统计和月度结余。",
        "帮我做一个每日计划，能打卡、记录心情和查看连续天数。",
        "帮我做一个读书清单，支持进度、摘录和阅读统计。",
      ],
    };
  },
  computed: {
    isLoggedIn() {
      return !!this.store.token;
    },
    isGenerating() {
      return (
        !!this.selected &&
        ["queued", "processing"].includes(this.selected.status)
      );
    },
    generationFailed() {
      return !!this.selected && this.selected.status === "failed";
    },
  },
  mounted() {
    document.title = "个人工作台";
    if (this.isLoggedIn) this.loadList();
  },
  beforeDestroy() {
    this.stopGenerationPolling();
    if (this.modificationAbortController)
      this.modificationAbortController.abort();
  },
  watch: {
    "form.useCustomAi"(value) {
      this.saveCustomAiEnabled(value);
    },
    "regenerateForm.useCustomAi"(value) {
      this.saveCustomAiEnabled(value);
    },
    isLoggedIn(value) {
      if (value) this.loadList();
      else {
        this.stopGenerationPolling();
        this.activeTab = "home";
        this.viewMode = "preview";
        this.mobileNav = false;
        this.selected = null;
        this.source = "";
        this.previewSources = {};
        this.workbenches = [];
      }
    },
  },
  methods: {
    goBack() {
      window.history.back();
    },
    saveCustomAiEnabled(value) {
      try {
        window.localStorage.setItem(
          CUSTOM_AI_ENABLED_STORAGE_KEY,
          String(Boolean(value))
        );
      } catch (error) {
        // Browsers can deny localStorage access in restricted contexts.
      }
    },
    onLogin() {
      this.showHome();
      this.loadList();
    },
    redirectToLogin() {
      redirectToRainseeLogin(window.location.href);
    },
    async loadList() {
      this.loading = true;
      try {
        this.workbenches = await listWorkbenches();
        this.previewSources = {};
        this.loadCardPreviews(this.workbenches);
      } catch (error) {
        this.showError(error);
      } finally {
        this.loading = false;
      }
    },
    showHome() {
      this.stopGenerationPolling();
      if (this.modificationAbortController)
        this.modificationAbortController.abort();
      this.modificationRunId += 1;
      this.modificationAbortController = null;
      this.modificationPublishing = false;
      this.modifying = false;
      this.activeTab = "home";
      this.viewMode = "preview";
      this.mobileNav = false;
      this.contentLoading = false;
      this.selected = null;
      this.source = "";
      this.versions = [];
      this.modification = "";
      this.modificationSteps = [];
    },
    loadCardPreviews(items) {
      items
        .filter((item) => item.status === "ready")
        .forEach(async (item) => {
          try {
            const html = await getWorkbenchContent(item.id);
            if (this.workbenches.some((current) => current.id === item.id))
              this.$set(this.previewSources, item.id, html);
          } catch (error) {
            this.$delete(this.previewSources, item.id);
          }
        });
    },
    resetCreate() {
      this.stopGenerationPolling();
      if (this.modificationAbortController)
        this.modificationAbortController.abort();
      this.activeTab = "create";
      this.selected = null;
      this.viewMode = "preview";
      this.contentLoading = false;
      this.modification = "";
      this.modificationSteps = [];
      this.modificationRunId += 1;
      this.modificationAbortController = null;
      this.modificationPublishing = false;
      this.modifying = false;
      this.form.prompt = "";
      this.form.name = "";
      this.mobileNav = false;
    },
    async create() {
      if (!this.form.prompt || this.creating) return;
      this.creating = true;
      this.notice = "";
      try {
        const data = await createWorkbench(this.aiPayload(this.form));
        this.workbenches.unshift(data);
        await this.openWorkbench(data.id);
        this.showNotice("生成任务已提交，可离开页面后稍后查看");
      } catch (error) {
        this.showError(error);
      } finally {
        this.creating = false;
      }
    },
    beginRegenerate() {
      if (!this.selected || this.isGenerating || !this.generationFailed) return;
      this.regenerateForm = {
        name: this.selected.name || "",
        prompt: this.selected.prompt || "",
        endpoint: this.form.endpoint || "",
        model: this.form.model || "",
        apiKey: this.form.apiKey || "",
        useCustomAi: this.form.useCustomAi,
      };
      this.viewMode = "regenerate";
    },
    async submitRegenerate() {
      if (!this.selected || !this.regenerateForm.prompt || this.regenerating)
        return;
      this.regenerating = true;
      this.notice = "";
      try {
        const data = await regenerateWorkbench(this.aiPayload({
          id: this.selected.id,
          ...this.regenerateForm,
          html: this.source,
        }));
        this.selected = { ...this.selected, ...data };
        this.workbenches = this.workbenches.map((item) =>
          item.id === data.id ? { ...item, ...data, html: undefined } : item
        );
        this.viewMode = "preview";
        this.startGenerationPolling();
        this.showNotice("当前工作台已重新提交生成");
      } catch (error) {
        this.showError(error);
      } finally {
        this.regenerating = false;
      }
    },
    async submitModification() {
      if (!this.selected || !this.modification || this.modifying) return;
      const runId = ++this.modificationRunId;
      const instruction = this.modification;
      const history = [];
      const controller = new AbortController();
      let appliedChanges = 0;
      let pendingStepIndex = -1;
      this.modifying = true;
      this.modificationAbortController = controller;
      this.modificationSteps.push({
        title: "修改要求",
        message: instruction,
        detail: "",
      });
      this.scrollModificationStepsToBottom();
      this.notice = "";
      try {
        for (let round = 0; round < 16; round += 1) {
          pendingStepIndex = this.modificationSteps.length;
          this.modificationSteps.push({
            title: "等待 AI 指令",
            message: `正在根据前 ${round} 个工具结果决定下一步…`,
            detail: "",
            pending: true,
          });
          this.scrollModificationStepsToBottom();
          const command = await modifyWorkbenchStep(
            {
              id: this.selected.id,
              instruction,
              history,
              ...this.selectedAiConfig(),
            },
            controller.signal
          );
          if (runId !== this.modificationRunId) return;

          const execution = this.executeModificationTool(command);
          if (execution.applied) appliedChanges += 1;
          this.$set(
            this.modificationSteps,
            pendingStepIndex,
            this.presentModificationStep(command, execution.result)
          );
          this.scrollModificationStepsToBottom();
          pendingStepIndex = -1;

          if (command.tool === "finish_edit") {
            if (!appliedChanges) throw new Error("AI 没有对源码执行任何修改");
            this.modificationPublishing = true;
            this.modificationAbortController = null;
            const data = await saveWorkbench({
              id: this.selected.id,
              html: this.source,
              require_store: true,
              version_label: "AI 工具修改",
            });
            if (runId !== this.modificationRunId) return;
            this.selected = { ...this.selected, ...data };
            this.workbenches = this.workbenches.map((item) =>
              item.id === data.id ? { ...item, ...data, html: undefined } : item
            );
            await this.loadWorkbenchSource(data.id);
            await this.loadVersions();
            this.modification = "";
            this.modificationSteps.push({
              title: "保存发布完成",
              message: "当前修改已应用到工作台",
              detail: "",
            });
            this.scrollModificationStepsToBottom();
            this.showNotice("AI 修改已保存并发布");
            return;
          }
          history.push({
            tool: command.tool,
            arguments: command.arguments || {},
            result: execution.result,
          });
        }
        throw new Error("AI 修改超过 16 个工具步骤，已停止且未发布");
      } catch (error) {
        if (runId !== this.modificationRunId) return;
        const failedStep = {
          title: "修改已停止",
          message: error.message || "AI 修改失败",
          detail: "",
          failed: true,
        };
        if (pendingStepIndex >= 0)
          this.$set(this.modificationSteps, pendingStepIndex, failedStep);
        else this.modificationSteps.push(failedStep);
        this.scrollModificationStepsToBottom();
        this.showError(error);
      } finally {
        if (runId === this.modificationRunId) {
          this.modifying = false;
          this.modificationAbortController = null;
          this.modificationPublishing = false;
          this.scrollModificationStepsToBottom(true);
        }
      }
    },
    cancelModification() {
      if (this.modificationAbortController)
        this.modificationAbortController.abort();
      this.modificationRunId += 1;
      this.modifying = false;
      this.modificationAbortController = null;
      this.modificationPublishing = false;
      const stoppedStep = {
        title: "用户已停止",
        message: "当前工具请求结束后不再继续，尚未发布的源码修改保留在编辑器中。",
        detail: "",
      };
      let pendingIndex = -1;
      for (let index = this.modificationSteps.length - 1; index >= 0; index -= 1) {
        if (this.modificationSteps[index].pending) {
          pendingIndex = index;
          break;
        }
      }
      if (pendingIndex >= 0)
        this.$set(this.modificationSteps, pendingIndex, stoppedStep);
      else this.modificationSteps.push(stoppedStep);
      this.scrollModificationStepsToBottom(true);
    },
    scrollModificationStepsToBottom(focusInput = false) {
      this.$nextTick(() => {
        const steps = this.$refs.modificationSteps;
        if (steps) steps.scrollTop = steps.scrollHeight;
        if (focusInput && this.$refs.modificationInput)
          this.$refs.modificationInput.focus();
      });
    },
    executeModificationTool(command) {
      const tool = command && command.tool;
      const args = (command && command.arguments) || {};
      const toolSource = this.sourceForModificationTools();
      if (tool === "search_source") {
        const query = String(args.query || "");
        const sourceLower = toolSource.toLocaleLowerCase();
        const queryLower = query.toLocaleLowerCase();
        const matches = [];
        let index = 0;
        let line = 1;
        let cursor = 0;
        let total = 0;
        while ((index = sourceLower.indexOf(queryLower, index)) !== -1) {
          line += (toolSource.slice(cursor, index).match(/\n/g) || []).length;
          total += 1;
          if (matches.length < 20) {
            matches.push({
              line,
              snippet: toolSource
                .slice(Math.max(0, index - 100), index + query.length + 140)
                .replace(/\s+/g, " ")
                .slice(0, 360),
            });
          }
          cursor = index;
          index += Math.max(query.length, 1);
        }
        return {
          result: {
            ok: true,
            query,
            total_matches: total,
            matches,
            truncated: total > matches.length,
          },
        };
      }
      if (tool === "read_source") {
        const lines = toolSource.split(/\r?\n/);
        const start = Math.max(1, Number(args.start_line) || 1);
        const end = Math.min(
          lines.length,
          Math.max(start, Math.min(Number(args.end_line) || start, start + 159))
        );
        let budget = 28000;
        const selectedLines = [];
        for (let index = start - 1; index < end && budget > 0; index += 1) {
          const original = lines[index] || "";
          const text = original.slice(0, Math.min(original.length, budget, 4000));
          selectedLines.push({
            line: index + 1,
            text,
            truncated: text.length < original.length,
          });
          budget -= text.length + 40;
        }
        const result = {
          ok: true,
          start_line: start,
          end_line: selectedLines.length
            ? selectedLines[selectedLines.length - 1].line
            : start,
          total_lines: lines.length,
          lines: selectedLines,
          truncated: selectedLines.length < end - start + 1,
        };
        return { result };
      }
      if (tool === "replace_source") {
        const oldText = String(args.old_text || "");
        const newText = String(args.new_text || "");
        if (`${oldText}\n${newText}`.toLocaleLowerCase().includes("data-aihelp-kv")) {
          return {
            result: {
              ok: false,
              applied: false,
              error: "系统 KV 运行时禁止修改",
            },
          };
        }
        let matchText = oldText;
        let replacementText = newText;
        let occurrences = oldText ? this.source.split(matchText).length - 1 : 0;
        if (!occurrences && oldText.includes("\n")) {
          const crlfText = oldText.replace(/\n/g, "\r\n");
          occurrences = this.source.split(crlfText).length - 1;
          if (occurrences) {
            matchText = crlfText;
            replacementText = newText.replace(/\n/g, "\r\n");
          }
        }
        if (occurrences !== 1) {
          return {
            result: {
              ok: false,
              applied: false,
              occurrences,
              error:
                occurrences === 0
                  ? "精确旧文本未找到，请重新读取源码"
                  : "精确旧文本出现多次，请扩大上下文后重试",
            },
          };
        }
        this.source = this.source.replace(matchText, replacementText);
        this.$nextTick(this.refreshPreview);
        return {
          applied: true,
          result: {
            ok: true,
            applied: true,
            removed_chars: oldText.length,
            inserted_chars: newText.length,
          },
        };
      }
      if (tool === "finish_edit") {
        return {
          result: {
            ok: true,
            summary: String(args.summary || "修改完成"),
          },
        };
      }
      throw new Error("AI 返回了不支持的源码工具");
    },
    aiPayload(payload) {
      const normalized = { ...payload };
      const enabled = Boolean(normalized.useCustomAi);
      delete normalized.useCustomAi;
      if (!enabled) {
        normalized.endpoint = "";
        normalized.model = "";
        normalized.apiKey = "";
      }
      return normalized;
    },
    selectedAiConfig() {
      return this.aiPayload({
        endpoint: this.form.endpoint,
        model: this.form.model,
        apiKey: this.form.apiKey,
        useCustomAi: this.form.useCustomAi,
      });
    },
    presentModificationStep(command, result) {
      const args = command.arguments || {};
      const reason = args.reason ? `，${args.reason}` : "";
      let title = "执行工具";
      let message = reason.replace(/^，/, "");
      if (command.tool === "search_source") {
        title = "搜索源码";
        message = `查找“${args.query}”，找到 ${result.total_matches || 0} 处${reason}`;
      } else if (command.tool === "read_source") {
        title = "读取源码";
        message = `读取第 ${result.start_line}-${result.end_line} 行${reason}`;
      } else if (command.tool === "replace_source") {
        title = result.applied ? "修改源码" : "替换未执行";
        message = result.applied
          ? `${args.reason || "已完成一次精确替换"}`
          : result.error || "精确替换失败";
      } else if (command.tool === "finish_edit") {
        title = "完成修改";
        message = result.summary || "准备校验并发布";
      }
      const detailArgs = { ...args };
      if (command.tool === "replace_source") {
        detailArgs.old_text = this.previewToolText(detailArgs.old_text);
        detailArgs.new_text = this.previewToolText(detailArgs.new_text);
      }
      return {
        title,
        message,
        failed: result.ok === false,
        detail: JSON.stringify(
          { command: command.tool, arguments: detailArgs, result },
          null,
          2
        ),
      };
    },
    previewToolText(value) {
      const text = String(value || "");
      return text.length > 1200 ? `${text.slice(0, 1200)}\n…已省略` : text;
    },
    sourceForModificationTools() {
      return this.source.replace(
        /<script[^>]+data-aihelp-kv=["']1["'][^>]*>[\s\S]*?<\/script>/gi,
        (runtime) => {
          const lineBreaks = (runtime.match(/\n/g) || []).length;
          return `<!-- data-aihelp-kv 系统运行时已隐藏 -->${"\n".repeat(
            lineBreaks
          )}`;
        }
      );
    },
    async importProject(event) {
      const file = event.target.files && event.target.files[0];
      event.target.value = "";
      if (!file || this.importing) return;
      if (file.size > 12 * 1024 * 1024) {
        this.showError(new Error("导入文件不能超过 12MB"));
        return;
      }
      this.importing = true;
      this.notice = "";
      try {
        const data = await importWorkbench(file, this.form.name);
        this.workbenches.unshift({ ...data, html: undefined });
        await this.openWorkbench(data.id);
        this.showNotice("项目已导入并发布");
      } catch (error) {
        this.showError(error);
      } finally {
        this.importing = false;
      }
    },
    async openWorkbench(id) {
      this.stopGenerationPolling();
      if (this.modificationAbortController)
        this.modificationAbortController.abort();
      this.modificationRunId += 1;
      this.modificationAbortController = null;
      this.modificationPublishing = false;
      this.modifying = false;
      this.contentLoading = false;
      this.source = "";
      try {
        const data = await getWorkbench(id);
        this.selected = data;
        this.modification = "";
        this.modificationSteps = [];
        this.activeTab = "preview";
        this.viewMode = "preview";
        this.mobileNav = false;
        if (["queued", "processing"].includes(data.status))
          this.startGenerationPolling();
        else if (data.status === "ready" || !data.status) {
          await this.loadWorkbenchSource(data.id);
          await this.loadVersions();
        } else if (data.status === "failed")
          this.showError(new Error(data.error_message || "生成失败"));
      } catch (error) {
        this.showError(error);
      }
    },
    async loadWorkbenchSource(id) {
      this.contentLoading = true;
      try {
        const html = await getWorkbenchContent(id);
        if (!this.selected || this.selected.id !== id) return;
        this.source = html;
        this.$set(this.previewSources, id, html);
      } finally {
        if (this.selected && this.selected.id === id)
          this.contentLoading = false;
      }
    },
    startGenerationPolling() {
      this.stopGenerationPolling();
      this.generationPoll = window.setInterval(this.refreshGeneration, 1500);
    },
    stopGenerationPolling() {
      if (this.generationPoll) window.clearInterval(this.generationPoll);
      this.generationPoll = null;
      this.generationPolling = false;
    },
    async refreshGeneration() {
      if (!this.selected || this.generationPolling) return;
      this.generationPolling = true;
      try {
        const data = await getWorkbench(this.selected.id);
        this.selected = data;
        this.workbenches = this.workbenches.map((item) =>
          item.id === data.id ? { ...item, ...data, html: undefined } : item
        );
        if (data.status === "ready") {
          this.stopGenerationPolling();
          await this.loadWorkbenchSource(data.id);
          await this.loadVersions();
          if (data.error_message) {
            this.showError(new Error(`AI 修改失败：${data.error_message}`));
          } else {
            const wasModification = !!this.modification;
            this.modification = "";
            this.showNotice(
              wasModification ? "AI 修改已完成并发布" : "工作台已生成并发布"
            );
          }
        } else if (data.status === "failed") {
          this.stopGenerationPolling();
          this.showError(new Error(data.error_message || "生成失败"));
        }
      } catch (error) {
        this.stopGenerationPolling();
        this.showError(error);
      } finally {
        this.generationPolling = false;
      }
    },
    async removeWorkbench(item) {
      const confirmed = await showConfirm(`确定删除“${item.name}”吗？`, {
        title: "删除工作台",
        confirmText: "删除",
        dangerous: true,
      });
      if (!confirmed) return;
      try {
        await deleteWorkbench(item.id);
        if (this.selected && this.selected.id === item.id)
          this.stopGenerationPolling();
        this.workbenches = this.workbenches.filter(
          (current) => current.id !== item.id
        );
        this.$delete(this.previewSources, item.id);
        if (this.selected && this.selected.id === item.id) {
          this.showHome();
        }
        this.showNotice("工作台已删除");
      } catch (error) {
        this.showError(error);
      }
    },
    beginRename(item) {
      if (!item || this.renaming) return;
      this.renameTarget = item;
      this.renameName = item.name || "";
      this.$nextTick(() => {
        const input = this.$refs.renameInput;
        if (input) {
          input.focus();
          input.select();
        }
      });
    },
    cancelRename() {
      if (this.renaming) return;
      this.renameTarget = null;
      this.renameName = "";
    },
    async submitRename() {
      const name = this.renameName.trim();
      if (!this.renameTarget || !name || this.renaming) return;
      this.renaming = true;
      try {
        const data = await renameWorkbench(this.renameTarget.id, name);
        this.workbenches = this.workbenches.map((item) =>
          item.id === data.id ? { ...item, ...data, html: undefined } : item
        );
        if (this.selected && this.selected.id === data.id)
          this.selected = { ...this.selected, ...data };
        this.renameTarget = null;
        this.renameName = "";
        this.showNotice("工作台名称已更新");
      } catch (error) {
        this.showError(error);
      } finally {
        this.renaming = false;
      }
    },
    async loadVersions() {
      if (!this.selected || this.selected.status === "failed") return;
      try {
        this.versions = await getWorkbenchVersions(this.selected.id);
      } catch (error) {
        this.showError(error);
      }
    },
    async save() {
      if (!this.selected || this.selected.status !== "ready" || this.saving)
        return;
      this.saving = true;
      try {
        const data = await saveWorkbench({
          id: this.selected.id,
          html: this.source,
        });
        this.selected = { ...this.selected, ...data };
        this.workbenches = this.workbenches.map((item) =>
          item.id === data.id ? { ...item, ...data, html: undefined } : item
        );
        await this.loadWorkbenchSource(data.id);
        await this.loadVersions();
        this.showNotice("已保存并发布");
      } catch (error) {
        this.showError(error);
      } finally {
        this.saving = false;
      }
    },
    async restore(versionId) {
      const confirmed = await showConfirm("恢复后当前内容会被替换，确定继续吗？");
      if (!confirmed) return;
      try {
        const data = await restoreWorkbench(this.selected.id, versionId);
        this.selected = { ...this.selected, ...data };
        await this.loadWorkbenchSource(data.id);
        this.workbenches = this.workbenches.map((item) =>
          item.id === data.id ? { ...item, ...data, html: undefined } : item
        );
        await this.loadVersions();
        this.showNotice("已恢复版本");
      } catch (error) {
        this.showError(error);
      }
    },
    closeEditor() {
      this.viewMode = "preview";
    },
    refreshPreview() {
      const frame = this.$refs.preview;
      if (frame) frame.srcdoc = this.source;
    },
    logout() {
      this.stopGenerationPolling();
      mutations.logout();
      this.activeTab = "home";
      this.viewMode = "preview";
      this.mobileNav = false;
      this.selected = null;
      this.source = "";
      this.previewSources = {};
      this.workbenches = [];
    },
    formatDate(value) {
      if (!value) return "刚刚";
      const date = new Date(value);
      return Number.isNaN(date.getTime())
        ? value
        : date.toLocaleString("zh-CN", {
            month: "numeric",
            day: "numeric",
            hour: "2-digit",
            minute: "2-digit",
          });
    },
    showNotice(message) {
      this.notice = message;
      this.noticeType = "success";
      window.setTimeout(() => {
        this.notice = "";
      }, 3000);
    },
    showError(error) {
      this.notice = error.message || "操作失败";
      this.noticeType = "error";
    },
  },
};
</script>

<style scoped>
:global(*) {
  box-sizing: border-box;
}
.workbench-shell {
  min-height: 100vh;
  display: flex;
  background: #f6f7f9;
  color: #1d232e;
  font-family: Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif;
}
.workbench-sidebar {
  width: 256px;
  flex: 0 0 256px;
  position: sticky;
  top: 0;
  height: 100vh;
  background: #172033;
  color: #d4dae6;
  display: flex;
  flex-direction: column;
  padding: 28px 18px 20px;
  overflow: hidden;
  z-index: 50;
}
.sidebar-brand {
  display: flex;
  align-items: center;
  gap: 11px;
  margin: 0 10px 34px;
}
.sidebar-brand strong {
  display: block;
  color: #fff;
  font-size: 16px;
}
.sidebar-brand span {
  display: block;
  color: #8e9bb1;
  font-size: 11px;
  margin-top: 3px;
}
.brand-mark {
  width: 46px;
  height: 46px;
  border-radius: 12px;
  background: #6d5dfc;
  color: #fff;
  display: grid;
  place-items: center;
  font-weight: 800;
  font-size: 20px;
  box-shadow: 0 8px 22px rgba(109, 93, 252, 0.25);
}
.brand-mark.small {
  width: 36px;
  height: 36px;
  border-radius: 9px;
  font-size: 16px;
}
button {
  font-family: inherit;
}
.new-workbench,
.primary-btn {
  border: 0;
  cursor: pointer;
  font-weight: 700;
}
.sidebar-home,
.sidebar-close,
.sidebar-backdrop {
  border: 0;
  font: inherit;
}
.sidebar-home {
  display: flex;
  width: 100%;
  min-height: 42px;
  align-items: center;
  margin-bottom: 10px;
  padding: 10px 12px;
  border-radius: 8px;
  background: transparent;
  color: #aab4c6;
  cursor: pointer;
  font-size: 13px;
  text-align: left;
}
.sidebar-home:hover,
.sidebar-home.active {
  background: #27334a;
  color: #fff;
}
.sidebar-close,
.sidebar-backdrop {
  display: none;
}
.new-workbench {
  width: 100%;
  background: #6d5dfc;
  color: #fff;
  border-radius: 9px;
  padding: 13px 14px;
  text-align: left;
  font-size: 14px;
  box-shadow: 0 8px 20px rgba(109, 93, 252, 0.2);
}
.new-workbench span {
  font-size: 20px;
  vertical-align: -1px;
  margin-right: 9px;
}
.workbench-tabs {
  margin-top: 25px;
}
.workbench-tabs button,
.sidebar-home,
.sidebar-foot button {
  border: 0;
  background: none;
  color: #aab4c6;
  width: 100%;
  text-align: left;
  padding: 12px;
  border-radius: 8px;
  cursor: pointer;
  font-size: 13px;
}
.workbench-tabs button.active,
.workbench-tabs button:hover,
.sidebar-home.active,
.sidebar-home:hover {
  background: #27334a;
  color: #fff;
}
.tab-icon {
  display: inline-block;
  width: 22px;
  color: #8f82ff;
  font-size: 17px;
}
.workbench-tabs em {
  float: right;
  font-style: normal;
  color: #77849a;
  font-size: 12px;
}
.sidebar-foot {
  margin-top: auto;
  display: flex;
  flex-direction: column;
  gap: 4px;
  border-top: 1px solid #2b374c;
  padding-top: 16px;
}
.sidebar-foot a,
.sidebar-foot button {
  color: #9aa7bb;
  text-decoration: none;
  padding: 9px 12px;
  font-size: 12px;
}
.workbench-main {
  flex: 1;
  min-width: 0;
  padding: 42px clamp(24px, 5vw, 72px);
  overflow: auto;
}
.workbench-header {
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  margin-bottom: 40px;
}
.header-leading {
  display: flex;
  align-items: center;
  min-width: 0;
}
.mobile-back-button {
  display: none;
}
.eyebrow,
.step-label {
  color: #7a6cff;
  font-size: 11px;
  font-weight: 800;
  letter-spacing: 0.12em;
}
.workbench-header h1 {
  font-size: 32px;
  margin: 8px 0 0;
  letter-spacing: -0.02em;
}
.notice {
  font-size: 13px;
  padding: 9px 13px;
  border-radius: 7px;
}
.notice.success {
  background: #e5f7ee;
  color: #177c4d;
}
.notice.error {
  background: #fff0ef;
  color: #bd3f36;
}
.mobile-menu {
  display: none;
}
.workspace-list,
.create-panel,
.editor-view {
  max-width: 1160px;
  margin: auto;
}
.list-intro {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 25px;
}
h2 {
  margin: 0;
  font-size: 22px;
}
.list-intro p,
.create-copy p {
  color: #778191;
  font-size: 14px;
  margin: 8px 0 0;
}
.primary-btn {
  background: #6d5dfc;
  color: #fff;
  border-radius: 8px;
  padding: 12px 17px;
}
.primary-btn:hover {
  background: #5d4dec;
}
.primary-btn:disabled {
  opacity: 0.55;
  cursor: not-allowed;
}
.primary-btn.small {
  padding: 9px 13px;
  font-size: 12px;
}
.workspace-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(245px, 1fr));
  gap: 18px;
}
.workspace-card {
  background: #fff;
  border: 1px solid #e5e8ee;
  border-radius: 10px;
  overflow: hidden;
  cursor: pointer;
  transition: transform 0.18s, box-shadow 0.18s;
}
.workspace-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 10px 26px rgba(30, 45, 70, 0.08);
}
.card-preview {
  height: 160px;
  background: #eef1f6;
  position: relative;
  overflow: hidden;
}
.card-preview iframe {
  width: 100%;
  height: 100%;
  border: 0;
  background: #fff;
  pointer-events: none;
}
.preview-placeholder {
  position: absolute;
  inset: 0;
  display: grid;
  place-items: center;
  color: #9aa5b6;
  font-size: 13px;
}
.card-body {
  padding: 15px 16px;
}
.card-title {
  display: flex;
  align-items: center;
  gap: 8px;
}
.card-title h3 {
  font-size: 15px;
  margin: 0;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.status-dot {
  width: 7px;
  height: 7px;
  background: #2ebb76;
  border-radius: 50%;
  display: inline-block;
  flex: none;
}
.card-body p {
  color: #7d8795;
  font-size: 12px;
  line-height: 1.5;
  height: 36px;
  overflow: hidden;
  margin: 9px 0;
}
.card-body time {
  color: #a0a8b4;
  font-size: 11px;
}
.empty-state,
.loading-state {
  text-align: center;
  padding: 90px 20px;
  background: #fff;
  border: 1px dashed #dce1e9;
  border-radius: 10px;
}
.empty-icon {
  color: #776aff;
  font-size: 34px;
}
.empty-state h3 {
  margin: 14px 0 5px;
}
.empty-state p {
  color: #7d8795;
  font-size: 13px;
  margin-bottom: 20px;
}
.create-panel {
  background: #fff;
  border: 1px solid #e6e9ef;
  border-radius: 12px;
  padding: 42px clamp(24px, 6vw, 78px);
}
.create-copy h2 {
  font-size: 30px;
  margin-top: 13px;
}
.prompt-form {
  max-width: 760px;
  margin-top: 34px;
}
label {
  display: block;
  color: #4b5565;
  font-size: 13px;
  font-weight: 700;
  margin-bottom: 10px;
}
textarea,
input {
  width: 100%;
  border: 1px solid #dfe3ea;
  border-radius: 7px;
  background: #fbfcfd;
  color: #1d232e;
  font: inherit;
  font-size: 14px;
  outline: none;
}
textarea:focus,
input:focus {
  border-color: #8a7eff;
  box-shadow: 0 0 0 3px rgba(109, 93, 252, 0.1);
}
.prompt-form textarea {
  resize: vertical;
  line-height: 1.6;
  padding: 15px;
}
input {
  padding: 11px 12px;
  margin-top: 8px;
}
.form-row {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;
  margin-top: 18px;
}
.form-row.single-column {
  grid-template-columns: 1fr;
}
.advanced-config {
  margin-top: 22px;
  border-top: 1px solid #edf0f4;
  padding-top: 15px;
}
.advanced-config summary {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  cursor: pointer;
  color: #687385;
  font-size: 12px;
}
.advanced-config fieldset {
  margin: 0;
  padding: 0;
  border: 0;
}
.advanced-config fieldset:disabled {
  opacity: 0.55;
}
.custom-ai-toggle {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  margin: 0;
  color: #596577;
  cursor: pointer;
  font-size: 12px;
}
.custom-ai-toggle input {
  width: 16px;
  height: 16px;
  margin: 0;
  padding: 0;
  accent-color: #6d5dfc;
}
.create-btn {
  margin-top: 28px;
  font-size: 14px;
  padding: 13px 19px;
}
.create-btn span {
  margin-left: 16px;
  font-size: 18px;
}
.prompt-examples {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-top: 30px;
  color: #8993a1;
  font-size: 12px;
  align-items: center;
}
.prompt-examples button {
  color: #697486;
  background: #f4f5f8;
  border: 0;
  padding: 8px 11px;
  border-radius: 6px;
  cursor: pointer;
  font-size: 12px;
}
.import-project {
  display: flex;
  align-items: center;
  gap: 14px;
  margin-top: 22px;
  padding-top: 18px;
  border-top: 1px solid #edf0f4;
}
.import-project > div {
  display: grid;
  flex: 1;
  gap: 3px;
}
.import-project strong {
  color: #4c5666;
  font-size: 13px;
}
.import-project span {
  color: #8a94a2;
  font-size: 11px;
}
.import-file {
  display: none;
}
.import-button {
  min-height: 38px;
  border: 1px solid #d7dce5;
  border-radius: 6px;
  background: #fff;
  color: #596577;
  cursor: pointer;
  font: inherit;
  font-size: 12px;
  font-weight: 700;
  padding: 0 12px;
}
.import-button:hover:not(:disabled) {
  border-color: #9186f7;
  color: #5c50d9;
}
.import-button:disabled {
  cursor: wait;
  opacity: 0.6;
}
.editor-toolbar {
  display: flex;
  align-items: center;
  gap: 18px;
  margin-bottom: 19px;
}
.back-button,
.toolbar-actions button,
.versions-head button,
.pane-head button {
  border: 0;
  background: none;
  color: #6c7788;
  cursor: pointer;
  font-size: 12px;
}
.editor-title {
  display: flex;
  align-items: center;
  gap: 8px;
  flex: 1;
}
.editor-title span:last-child {
  font-size: 11px;
  color: #9ba4b2;
}
.toolbar-actions {
  display: flex;
  gap: 10px;
  align-items: center;
}
.editor-layout {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 14px;
  min-height: 520px;
}
.editor-layout.preview-hidden {
  grid-template-columns: 1fr;
}
.code-pane,
.preview-pane {
  background: #202938;
  border-radius: 9px;
  overflow: hidden;
  display: flex;
  flex-direction: column;
}
.preview-pane {
  background: #fff;
  border: 1px solid #e1e5eb;
}
.pane-head {
  height: 42px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 14px;
  color: #aab5c5;
  background: #273347;
  font-size: 11px;
}
.preview-pane .pane-head {
  background: #f5f6f8;
  color: #667285;
}
.code-pane textarea {
  flex: 1;
  resize: none;
  border: 0;
  border-radius: 0;
  background: #202938;
  color: #e4e8ef;
  font: 12px/1.65 "SFMono-Regular", Consolas, monospace;
  padding: 17px;
  outline: none;
  tab-size: 2;
}
.preview-pane iframe {
  flex: 1;
  width: 100%;
  border: 0;
  background: #fff;
}
.versions-panel {
  margin-top: 25px;
  background: #fff;
  border: 1px solid #e5e8ee;
  border-radius: 9px;
  padding: 20px;
}
.versions-head {
  display: flex;
  justify-content: space-between;
  align-items: start;
}
.versions-head h3 {
  margin: 0;
  font-size: 15px;
}
.versions-head p {
  margin: 6px 0 0;
  color: #8893a2;
  font-size: 12px;
}
.version-list {
  margin-top: 16px;
  border-top: 1px solid #edf0f4;
}
.version-row {
  padding: 13px 0;
  border-bottom: 1px solid #edf0f4;
  display: flex;
  justify-content: space-between;
  align-items: center;
}
.version-row strong,
.version-row span {
  display: block;
}
.version-row strong {
  font-size: 13px;
}
.version-row span {
  font-size: 11px;
  color: #9aa3af;
  margin-top: 5px;
}
.version-row button {
  border: 1px solid #dce2eb;
  background: #fff;
  border-radius: 6px;
  padding: 7px 10px;
  color: #626d7e;
  cursor: pointer;
  font-size: 11px;
}
.versions-empty {
  color: #a1a9b5;
  font-size: 12px;
  padding-top: 18px;
}
.workbench-login {
  width: 100%;
  display: grid;
  place-items: center;
  background: linear-gradient(135deg, #f3f2ff, #f7f8fb);
}
.login-card {
  width: min(420px, calc(100% - 32px));
  background: #fff;
  border: 1px solid #e7e9ef;
  border-radius: 13px;
  padding: 42px 32px;
  text-align: center;
  box-shadow: 0 18px 45px rgba(35, 45, 80, 0.09);
}
.login-card .brand-mark {
  margin: 0 auto 20px;
}
.login-card h1 {
  font-size: 25px;
  margin: 0;
}
.login-card p {
  color: #7b8594;
  font-size: 13px;
  margin: 10px 0 22px;
}
.login-redirect-button {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-height: 42px;
  padding: 0 22px;
  border: 0;
  border-radius: 10px;
  background: #007aff;
  color: #fff;
  font-size: 14px;
  cursor: pointer;
}
.back-link {
  display: inline-block;
  color: #7b6dfb;
  text-decoration: none;
  font-size: 12px;
  margin-top: 18px;
}
.mobile-nav {
  display: none;
}
@media (max-width: 800px) {
  .workbench-shell {
    display: block;
  }
  .workbench-sidebar {
    width: min(82vw, 300px);
    height: 100dvh;
    padding: calc(18px + env(safe-area-inset-top)) 16px
      calc(18px + env(safe-area-inset-bottom));
    position: fixed;
    inset: 0 auto 0 0;
    display: flex;
    transform: translateX(-100%);
    visibility: hidden;
    transition: transform 0.2s ease, visibility 0.2s;
    box-shadow: 18px 0 42px rgba(10, 16, 28, 0.24);
    z-index: 70;
  }
  .workbench-sidebar.mobile-open {
    transform: translateX(0);
    visibility: visible;
  }
  .sidebar-brand {
    margin: 0 4px 24px;
  }
  .sidebar-brand > div:nth-child(2) {
    min-width: 0;
    flex: 1;
  }
  .sidebar-close {
    display: grid;
    width: 40px;
    height: 40px;
    flex: 0 0 40px;
    place-items: center;
    border-radius: 7px;
    background: transparent;
    color: #aab4c6;
    cursor: pointer;
    font-size: 25px;
  }
  .sidebar-backdrop {
    display: block;
    position: fixed;
    inset: 0;
    background: rgba(15, 22, 34, 0.48);
    cursor: pointer;
    z-index: 60;
  }
  .sidebar-foot {
    display: flex;
  }
  .workbench-main {
    padding: 25px 16px 40px;
  }
  .workbench-header {
    gap: 10px;
    margin-bottom: 25px;
    align-items: center;
  }
  .header-leading {
    flex: 1;
    gap: 10px;
  }
  .header-leading > div {
    min-width: 0;
  }
  .header-actions {
    flex: 0 0 auto;
  }
  .mobile-back-button {
    width: 40px;
    height: 40px;
    flex: 0 0 40px;
    display: grid;
    place-items: center;
    border: 1px solid #dfe3e9;
    border-radius: 7px;
    background: #fff;
    color: #465267;
    cursor: pointer;
    text-decoration: none;
  }
  .mobile-back-button svg {
    width: 22px;
    height: 22px;
    fill: none;
    stroke: currentColor;
    stroke-width: 2;
    stroke-linecap: round;
    stroke-linejoin: round;
  }
  .workbench-header h1 {
    font-size: 24px;
  }
  .mobile-menu {
    display: block;
    border: 0;
    background: #eef0f5;
    border-radius: 7px;
    padding: 8px 11px;
    color: #596578;
  }
  .notice {
    display: none;
  }
  .list-intro {
    display: block;
  }
  .list-intro .primary-btn {
    margin-top: 18px;
  }
  .create-panel {
    padding: 27px 19px;
  }
  .create-copy h2 {
    font-size: 24px;
  }
  .form-row {
    grid-template-columns: 1fr;
    gap: 0;
  }
  .workspace-grid {
    grid-template-columns: 1fr;
  }
  .editor-toolbar {
    flex-wrap: wrap;
    gap: 10px;
  }
  .editor-title {
    order: 3;
    flex-basis: 100%;
  }
  .toolbar-actions {
    margin-left: auto;
  }
  .editor-layout {
    grid-template-columns: 1fr;
    min-height: 460px;
  }
  .preview-pane {
    min-height: 360px;
  }
  .version-row {
    gap: 10px;
  }
}
.rename-workbench,
.delete-workbench {
  margin-left: auto;
  width: 28px;
  height: 28px;
  border: 0;
  border-radius: 6px;
  background: transparent;
  color: #9aa4b2;
  font-size: 20px;
  line-height: 1;
  cursor: pointer;
}
.rename-workbench {
  margin-left: auto;
  font-size: 16px;
}
.delete-workbench {
  margin-left: 0;
}
.rename-workbench:hover {
  background: #eef0ff;
  color: #5143dc;
}
.delete-workbench:hover {
  background: #fff0ef;
  color: #bd3f36;
}
.rename-modal-backdrop {
  position: fixed;
  z-index: 100;
  inset: 0;
  display: grid;
  place-items: center;
  padding: 20px;
  background: rgba(23, 32, 51, 0.42);
}
.rename-modal {
  width: min(100%, 400px);
  padding: 24px;
  border-radius: 8px;
  background: #fff;
  box-shadow: 0 20px 55px rgba(21, 31, 48, 0.24);
}
.rename-modal h2 {
  margin-bottom: 20px;
  font-size: 19px;
}
.rename-modal label {
  display: block;
  margin-bottom: 8px;
  color: #586477;
  font-size: 13px;
  font-weight: 700;
}
.rename-modal input {
  box-sizing: border-box;
  width: 100%;
  height: 42px;
  padding: 0 12px;
  border: 1px solid #ccd4df;
  border-radius: 6px;
  color: #1d232e;
  font: inherit;
}
.rename-modal input:focus {
  outline: 2px solid #c9c3ff;
  border-color: #6d5dfc;
}
.rename-modal-actions {
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  margin-top: 22px;
}
.toolbar-actions a {
  color: #6d5dfc;
  text-decoration: none;
  font-size: 12px;
  padding: 8px 4px;
}
.workbench-header h1 {
  letter-spacing: 0;
}
.status-dot.pending {
  background: #f2a93b;
}
.status-dot.failed {
  background: #d9564d;
}
.generation-state {
  min-height: 520px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
  background: #fff;
  border: 1px solid #e5e8ee;
  border-radius: 9px;
  padding: 32px;
}
.generation-state h2 {
  margin: 20px 0 8px;
  font-size: 20px;
}
.generation-state p {
  max-width: 470px;
  margin: 0 0 14px;
  color: #778191;
  font-size: 13px;
  line-height: 1.6;
}
.generation-state small {
  color: #9ba4b2;
  font-size: 11px;
}
.generation-spinner {
  width: 38px;
  height: 38px;
  border: 3px solid #e7e9f7;
  border-top-color: #6d5dfc;
  border-radius: 50%;
  animation: workbench-spin 0.85s linear infinite;
}
.failed-state {
  border-color: #f0d7d4;
}
.failed-state h2 {
  color: #b8443c;
}
.failed-actions {
  display: flex;
  align-items: center;
  gap: 12px;
}
.failed-actions .danger-action {
  min-height: 40px;
  padding: 0 16px;
  border: 1px solid #e4b8b4;
  border-radius: 6px;
  background: #fff;
}
@keyframes workbench-spin {
  to {
    transform: rotate(360deg);
  }
}
.project-tabs {
  display: flex;
  flex-direction: column;
  gap: 4px;
  min-height: 0;
  overflow: auto;
}
.projects-label {
  padding: 0 12px 9px;
  color: #77849a;
  font-size: 11px;
}
.projects-label em {
  float: right;
  font-style: normal;
}
.project-tabs button {
  display: flex;
  align-items: center;
  gap: 8px;
}
.project-name {
  min-width: 0;
  flex: 1;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.project-status {
  width: 7px;
  height: 7px;
  flex: 0 0 7px;
  border-radius: 50%;
  background: #2ebb76;
}
.project-status.queued,
.project-status.processing {
  background: #f2a93b;
}
.project-status.failed {
  background: #d9564d;
}
.projects-loading {
  padding: 10px 12px;
  color: #7e8ba0;
  font-size: 12px;
}
.workspace-stage {
  min-height: calc(100vh - 84px);
  display: flex;
  flex-direction: column;
}
.workspace-stage.canvas-stage {
  height: 100vh;
  height: 100dvh;
  min-height: 0;
  overflow: hidden;
}
.preview-main {
  padding: 0;
  overflow: hidden;
}
.preview-main .workbench-header {
  display: none;
}
.stage-toolbar {
  height: 58px;
  flex: 0 0 58px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  padding: 0 22px;
  background: #fff;
  border-bottom: 1px solid #e4e8ee;
}
.stage-title,
.stage-actions {
  display: flex;
  align-items: center;
  gap: 10px;
  min-width: 0;
}
.stage-title h1 {
  margin: 0;
  overflow: hidden;
  font-size: 15px;
  line-height: 1.25;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.stage-title-name {
  display: flex;
  align-items: center;
  gap: 4px;
  min-width: 0;
}
.rename-title-action {
  flex: 0 0 auto;
  width: 24px;
  height: 24px;
  padding: 0;
  border: 0;
  border-radius: 4px;
  background: transparent;
  color: #6d5dfc;
  cursor: pointer;
  font-size: 17px;
  line-height: 1;
}
.rename-title-action:hover {
  background: #eef0ff;
  color: #5143dc;
}
.stage-title span:last-child {
  display: block;
  margin-top: 2px;
  color: #8a95a5;
  font-size: 11px;
}
.stage-actions a,
.text-action,
.icon-action {
  border: 0;
  color: #586477;
  background: transparent;
  cursor: pointer;
  font-size: 12px;
  text-decoration: none;
}
.danger-action {
  border: 0;
  border-left: 1px solid #e5e8ed;
  background: transparent;
  color: #bd4d45;
  cursor: pointer;
  font: inherit;
  font-size: 12px;
  padding-left: 10px;
}
.danger-action:hover {
  color: #9f332c;
}
.icon-action {
  width: 34px;
  height: 34px;
  border: 1px solid #dce2ea;
  border-radius: 6px;
  font-size: 18px;
  line-height: 1;
}
.icon-action:hover,
.text-action:hover {
  color: #5143dc;
  border-color: #a99eff;
}
.ai-modify-bar {
  flex: 0 0 auto;
  display: grid;
  grid-template-columns: auto minmax(180px, 1fr) auto;
  align-items: center;
  gap: 12px;
  padding: 12px 18px;
  background: #f8f9fb;
  border-bottom: 1px solid #e4e8ee;
}
.ai-modify-panel {
  margin-bottom: 18px;
  overflow: hidden;
  border: 1px solid #e1e5eb;
  border-radius: 8px;
  background: #fff;
}
.ai-modify-panel .ai-modify-bar {
  border-bottom: 0;
}
.ai-modify-bar label {
  margin: 0;
  color: #3e4857;
  white-space: nowrap;
}
.ai-modify-bar textarea {
  min-height: 44px;
  max-height: 92px;
  padding: 10px 12px;
  resize: vertical;
  line-height: 1.4;
}
.ai-modify-bar .primary-btn {
  min-height: 42px;
  white-space: nowrap;
}
.stop-modification {
  min-height: 42px;
  padding: 0 18px;
  background: #fff;
}
.agent-steps {
  max-height: 320px;
  overflow: auto;
  padding: 0 18px 14px;
  border-top: 1px solid #e8ebf0;
}
.agent-step {
  display: grid;
  grid-template-columns: 24px minmax(0, 1fr);
  gap: 10px;
  padding: 12px 0;
  border-bottom: 1px solid #edf0f4;
}
.agent-step:last-child {
  border-bottom: 0;
}
.agent-step-index {
  display: grid;
  width: 22px;
  height: 22px;
  place-items: center;
  border-radius: 50%;
  background: #e9f7ef;
  color: #18794e;
  font-size: 11px;
  font-weight: 700;
}
.agent-step.failed .agent-step-index {
  background: #fff0ef;
  color: #b8443c;
}
.agent-step.pending .agent-step-index {
  color: transparent;
  background: transparent;
  border: 2px solid #ddd9ff;
  border-top-color: #6d5dfc;
  animation: workbench-spin 0.85s linear infinite;
}
.agent-step strong {
  color: #313947;
  font-size: 13px;
}
.agent-step p {
  margin: 3px 0 0;
  color: #6f7a8a;
  font-size: 12px;
  line-height: 1.5;
}
.agent-step details {
  margin-top: 7px;
}
.agent-step summary {
  color: #6d5dfc;
  cursor: pointer;
  font-size: 11px;
}
.agent-step pre {
  max-height: 220px;
  margin: 7px 0 0;
  overflow: auto;
  padding: 10px;
  background: #f5f6f8;
  color: #3d4654;
  font-size: 11px;
  line-height: 1.45;
  white-space: pre-wrap;
  word-break: break-word;
}
.workbench-canvas {
  flex: 1;
  min-height: 0;
  background: #fff;
}
.workbench-canvas iframe {
  display: block;
  width: 100%;
  height: 100%;
  min-height: 0;
  border: 0;
  background: #fff;
}
.stage-empty {
  flex: 1;
  display: grid;
  place-content: center;
  justify-items: center;
  padding: 36px;
  text-align: center;
}
.stage-empty h2 {
  margin: 12px 0 6px;
}
.stage-empty p {
  margin: 0 0 22px;
  color: #7d8795;
  font-size: 13px;
}
.stage-status {
  flex: 1;
  min-height: auto;
  border: 0;
  border-radius: 0;
}
.regenerate-panel {
  width: min(780px, 100%);
  margin: 34px auto;
  padding: 28px;
  border: 1px solid #e4e8ee;
  border-radius: 8px;
  background: #fff;
}
.regenerate-heading {
  margin-bottom: 24px;
}
.regenerate-heading h2 {
  font-size: 20px;
}
.regenerate-heading p {
  margin: 8px 0 0;
  color: #7d8795;
  font-size: 13px;
  line-height: 1.6;
}
.regenerate-panel textarea {
  min-height: 150px;
  padding: 12px;
  resize: vertical;
  line-height: 1.6;
}
.regenerate-panel .advanced-config {
  margin-top: 18px;
}
.regenerate-actions {
  display: flex;
  justify-content: flex-end;
  align-items: center;
  gap: 14px;
  margin-top: 26px;
}
.edit-workspace {
  max-width: 1380px;
  width: 100%;
  margin: 0 auto;
  padding: 22px clamp(16px, 3vw, 42px) 40px;
}
.editor-toolbar.compact {
  margin-bottom: 14px;
}
.editor-toolbar.compact .editor-title {
  order: initial;
  flex-basis: auto;
}
@media (max-width: 800px) {
  .new-workbench {
    display: block;
    margin-bottom: 10px;
  }
  .project-tabs {
    flex: 1;
    flex-direction: column;
    overflow-x: hidden;
    overflow-y: auto;
    padding: 0 2px 10px 0;
  }
  .projects-label,
  .projects-loading {
    display: block;
  }
  .project-tabs button {
    width: 100%;
    min-width: 0;
    flex: 0 0 auto;
  }
  .stage-menu {
    flex: 0 0 40px;
  }
  .preview-main {
    padding: 0;
  }
  .stage-toolbar {
    height: 56px;
    padding: 0 14px;
  }
  .ai-modify-bar {
    grid-template-columns: minmax(0, 1fr);
    gap: 8px;
    padding: 10px 12px;
  }
  .ai-modify-bar label {
    grid-column: 1 / -1;
  }
  .ai-modify-bar textarea {
    font-size: 16px;
  }
  .ai-modify-bar .primary-btn {
    width: 100%;
  }
  .stop-modification {
    width: 100%;
  }
  .agent-steps {
    padding: 0 12px 12px;
  }
  .regenerate-panel {
    width: auto;
    margin: 16px;
    padding: 20px 16px;
  }
  .regenerate-panel .form-row {
    grid-template-columns: 1fr;
    gap: 0;
    margin-top: 12px;
  }
  .regenerate-actions {
    justify-content: space-between;
  }
  .stage-actions a {
    display: none;
  }
  .edit-workspace {
    padding: 16px;
  }
  .editor-toolbar.compact .editor-title {
    flex-basis: 100%;
  }
  .import-project {
    align-items: flex-start;
    flex-direction: column;
  }
  .import-button {
    width: 100%;
  }
}
</style>
